/**
 * TypeScript interfaces for database-backed lesson prompt management
 */

import { ContextScenario, Narrative } from './micro';

/**
 * Quick prompt can be a simple string or an object with label and message
 */
export type QuickPrompt = string | { label: string; message: string };

/**
 * Database record for lesson prompts
 */
export interface LessonPromptRecord {
  id: string;
  lesson_id: string;

  // Core prompts
  ai_prompt: string | null;
  chat_intro: string | null;
  intro_prompt: string | null;
  prompt_guidance: string | null;

  // Narrative fields
  narrative_problem: string | null;
  narrative_what: string | null;
  narrative_why: string | null;
  narrative_how: string | null;
  narrative_bridge: string | null;

  // JSON arrays
  context_scenarios: ContextScenario[] | null;
  quick_prompts: QuickPrompt[] | null;

  // Metadata
  is_active: boolean;
  version: number;
  created_at: string;
  updated_at: string;
  created_by: string | null;
  updated_by: string | null;
}

/**
 * Flattened prompt data for use in components (camelCase)
 */
export interface LessonPromptData {
  lessonId: string;
  aiPrompt?: string;
  chatIntro?: string;
  introPrompt?: string;
  promptGuidance?: string;
  narrative?: Narrative;
  narrativeBridge?: string;
  contextScenarios?: ContextScenario[];
  quickPrompts?: QuickPrompt[];
  isActive?: boolean;
  version?: number;
  updatedAt?: string;
  updatedBy?: string;
}

/**
 * Input for creating/updating a lesson prompt
 */
export interface LessonPromptInput {
  lessonId: string;
  aiPrompt?: string;
  chatIntro?: string;
  introPrompt?: string;
  promptGuidance?: string;
  narrativeProblem?: string;
  narrativeWhat?: string;
  narrativeWhy?: string;
  narrativeHow?: string;
  narrativeBridge?: string;
  contextScenarios?: ContextScenario[];
  quickPrompts?: QuickPrompt[];
  isActive?: boolean;
}

/**
 * Database record for prompt version history
 */
export interface LessonPromptHistoryRecord {
  id: string;
  lesson_prompt_id: string;
  lesson_id: string;
  previous_data: LessonPromptRecord;
  changed_by: string | null;
  changed_at: string;
  change_type: 'create' | 'update' | 'delete';
  change_summary: string | null;
}

/**
 * Flattened history entry for UI display
 */
export interface PromptHistoryEntry {
  id: string;
  lessonPromptId: string;
  lessonId: string;
  previousData: LessonPromptData;
  changedBy: string | null;
  changedByName?: string; // Resolved from profiles
  changedAt: string;
  changeType: 'create' | 'update' | 'delete';
  changeSummary: string | null;
}

/**
 * Lesson with prompt source information for admin list
 */
export interface LessonWithPromptStatus {
  id: string;
  title: string;
  displayTitle?: string;
  hasDbPrompt: boolean;
  promptSource: 'database' | 'file';
  lastUpdated?: string;
  updatedBy?: string;
  updatedByName?: string;
  version?: number;
}

/**
 * Transform database record to camelCase data object
 */
export function transformPromptRecord(record: LessonPromptRecord): LessonPromptData {
  return {
    lessonId: record.lesson_id,
    aiPrompt: record.ai_prompt ?? undefined,
    chatIntro: record.chat_intro ?? undefined,
    introPrompt: record.intro_prompt ?? undefined,
    promptGuidance: record.prompt_guidance ?? undefined,
    narrative: (record.narrative_problem || record.narrative_what || record.narrative_why || record.narrative_how)
      ? {
          problem: record.narrative_problem ?? '',
          what: record.narrative_what ?? '',
          why: record.narrative_why ?? '',
          how: record.narrative_how ?? '',
        }
      : undefined,
    narrativeBridge: record.narrative_bridge ?? undefined,
    contextScenarios: record.context_scenarios ?? undefined,
    quickPrompts: record.quick_prompts ?? undefined,
    isActive: record.is_active,
    version: record.version,
    updatedAt: record.updated_at,
    updatedBy: record.updated_by ?? undefined,
  };
}

/**
 * Transform camelCase input to database record format
 */
export function transformPromptInput(input: LessonPromptInput): Partial<LessonPromptRecord> {
  return {
    lesson_id: input.lessonId,
    ai_prompt: input.aiPrompt ?? null,
    chat_intro: input.chatIntro ?? null,
    intro_prompt: input.introPrompt ?? null,
    prompt_guidance: input.promptGuidance ?? null,
    narrative_problem: input.narrativeProblem ?? null,
    narrative_what: input.narrativeWhat ?? null,
    narrative_why: input.narrativeWhy ?? null,
    narrative_how: input.narrativeHow ?? null,
    narrative_bridge: input.narrativeBridge ?? null,
    context_scenarios: input.contextScenarios ?? null,
    quick_prompts: input.quickPrompts ?? null,
    is_active: input.isActive ?? true,
  };
}

/**
 * Transform history record to UI-friendly format
 */
export function transformHistoryRecord(record: LessonPromptHistoryRecord): PromptHistoryEntry {
  return {
    id: record.id,
    lessonPromptId: record.lesson_prompt_id,
    lessonId: record.lesson_id,
    previousData: transformPromptRecord(record.previous_data),
    changedBy: record.changed_by,
    changedAt: record.changed_at,
    changeType: record.change_type,
    changeSummary: record.change_summary,
  };
}
